/** Bulk Move to line on outline tasks — dialog picker, reparent command, two-route SSRM refresh. */
import { PlannedTaskOutlineReparentResult } from '@jms/model';
import { EntityDataService } from '@workbuddy/services/entities';
import { ToastService } from '@workbuddy/ui/services';
import { SemanticCommand } from '@workbuddy/ui/services/keyboard.shortcuts';
import { WorkBuddyDialogService } from '@workbuddy/ui/v2/blocks/dialog';
import { BulkCommandArgs, BulkCommandOutcome } from '@workbuddy/ui/v2/blocks/entity.board/context';
import { OutlineEntityKinds, OutlineRow } from '../outline.authoring.model';
import { isOutlineRootTask, outlineSourceLineIds } from '../outline.selection';
import { OutlineMoveToLineDialogComponent } from '../dialogs/outline.move.to.line';
import { OutlineRowPatcher } from './outline.bulk.schedule';

export const OUTLINE_MOVE_TO_LINE_ACTION = SemanticCommand.MoveToLine;

export interface OutlineMoveToLineDeps {
  /** The entity pair this run is acting on, so one handler serves the live outline and the template one. */
  kinds: OutlineEntityKinds;
  /** The server command that reparents a batch of tasks onto one line. */
  reparentCommand: string;
  dialogs: WorkBuddyDialogService;
  entityData: EntityDataService;
  store: OutlineRowPatcher;
  toasts: ToastService;
  parentId: string;
}

export async function handleOutlineMoveToLine(args: BulkCommandArgs<OutlineRow>, deps: OutlineMoveToLineDeps): Promise<BulkCommandOutcome> {

  if ((args.bulkActionRequest?.actionId ?? args.action) !== OUTLINE_MOVE_TO_LINE_ACTION) return { handled: false };
  const rows = args.data ?? [];
  const taskIds = rows.filter(row => isOutlineRootTask(row, deps.kinds)).map(row => row.sourceId);
  if (!taskIds.length) return { handled: true, clearSelection: false };

  const sourceLineIds = outlineSourceLineIds(rows, deps.kinds);
  const targetLineId = resolveTargetLineId(args.commandData) ?? await deps.dialogs.modal<string, OutlineMoveToLineDialogComponent>(OutlineMoveToLineDialogComponent, {
    width: 'min(360px, calc(100vw - 40px))',
    align: 'top',
    close: { backdrop: true },
    inputs: { entity: deps.kinds.line, parentId: deps.parentId, excludeIds: sourceLineIds.length === 1 ? sourceLineIds : [] },
    aria: { label: 'Move to line' },
  });
  if (!targetLineId) return { handled: true, clearSelection: false };

  try {
    const result = await deps.entityData.executeCommand<PlannedTaskOutlineReparentResult>(deps.kinds.outline, deps.reparentCommand, { taskIds, targetLineId });
    if (!result?.moved) {
      deps.toasts.info('Move to line', 'Selected tasks are already on that Service Line.');
      return { handled: true };
    }
    applyOutlineReparentResult(deps.store, result, deps.kinds);
    deps.toasts.success('Move to line', `Moved ${result.moved} task${result.moved === 1 ? '' : 's'}.`);
    return { handled: true };
  } catch {
    deps.toasts.error('Move to line', 'Action failed, please try again.');
    return { handled: true, clearSelection: false };
  }
}

export function resolveTargetLineId(commandData: unknown): string | undefined {

  if (!commandData || typeof commandData !== 'object') return undefined;
  const value = (commandData as { targetLineId?: unknown }).targetLineId;
  if (typeof value === 'string' && value) return value;
  if (value && typeof value === 'object' && typeof (value as { _id?: unknown })._id === 'string') return (value as { _id: string })._id;
  return undefined;
}

function applyOutlineReparentResult(store: OutlineRowPatcher, result: PlannedTaskOutlineReparentResult, kinds: OutlineEntityKinds): void {

  const { linePrefix, taskPrefix } = kinds;
  for (const line of result.lines ?? []) {
    store.patchDisplayedRow(`${linePrefix}${line.id}`, { hasChildren: line.hasChildren });
    store.refreshServerSideRoute?.([`${linePrefix}${line.id}`]);
  }
  for (const parent of result.parents ?? []) {
    store.patchDisplayedRow(`${taskPrefix}${parent.id}`, { hasChildren: parent.hasChildren });
    store.refreshServerSideRoute?.([`${linePrefix}${parent.lineId}`, `${taskPrefix}${parent.id}`]);
  }
}
